import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, ArrowLeft, Check, ShieldCheck, Upload, Loader2, Film } from "lucide-react";
import { toast } from "sonner";
import OnboardBackButton from "@/components/OnboardBackButton";
import AffiliationsPicker from "@/components/AffiliationsPicker";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;

const STEPS = ["Organization", "Slate", "Verification Docs", "Owner Account", "Approved"];

export default function OnboardProductionHouse() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Org details
  const [legal, setLegal]       = useState("");
  const [brand, setBrand]       = useState("");
  const [address, setAddress]   = useState("");
  const [gstin, setGstin]       = useState("");
  const [pan, setPan]           = useState("");
  // Owner credentials
  const [ownerName, setOwnerName]     = useState("");
  const [ownerEmail, setOwnerEmail]   = useState("");
  const [ownerPhone, setOwnerPhone]   = useState("");
  const [ownerPwd, setOwnerPwd]       = useState("");
  // Mock doc uploads (no actual upload in v1)
  const [docsAck, setDocsAck] = useState(false);
  const [sectionAffiliations, setSectionAffiliations] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [slate, setSlate] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/meta/project-types`)
      .then(({ data }) => setProjectTypes(
        (Array.isArray(data) ? data : []).filter((t) => t.key !== "event")
      ))
      .catch(() => {});
  }, []);

  const toggleSlate = (key) => {
    setSlate((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/onboard/org`, {
        org_type: "production_house",
        legal_name: legal,
        brand_name: brand || legal,
        registered_address: address,
        gstin, pan,
        owner_name:  ownerName,
        owner_email: ownerEmail,
        owner_phone: ownerPhone,
        owner_password: ownerPwd,
        section_affiliations: sectionAffiliations,
        slate_specialties: Array.from(slate),
      });
      await setToken(data.access_token);
      await refresh();
      setStep(4);
      toast.success(`Welcome — ${data.organization.brand_name} is now verified`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create the organization");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10" data-testid="onboard-ph-page">
      <OnboardBackButton step={step} onStepBack={back} label="Back to Options" />
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Production House · Sign Up</div>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight mt-2">Set Up Your Studio</h1>

      <Stepper step={step} />

      {step === 0 && (
        <Card title="Organization Details" testid="step-org">
          <Field label="Legal Name *" tid="ph-legal">
            <Input
              data-testid="ph-legal-input"
              value={legal} onChange={(e) => setLegal(e.target.value)}
              placeholder="e.g. Magic Lamp Studios Pvt Ltd"
              className="h-11 bg-black/40 border-white/15"
            />
          </Field>
          <Field label="Brand Name (public-facing)" tid="ph-brand">
            <Input
              data-testid="ph-brand-input"
              value={brand} onChange={(e) => setBrand(e.target.value)}
              placeholder="e.g. Magic Lamp"
              className="h-11 bg-black/40 border-white/15"
            />
          </Field>
          <Field label="Registered Address" tid="ph-address">
            <Textarea
              data-testid="ph-address-input"
              value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="Building, Street, City, State, PIN"
              className="bg-black/40 border-white/15"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="GSTIN" tid="ph-gstin">
              <Input
                data-testid="ph-gstin-input"
                value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                placeholder="27ABCDE1234F1Z5" maxLength={15}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
              />
            </Field>
            <Field label="PAN" tid="ph-pan">
              <Input
                data-testid="ph-pan-input"
                value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())}
                placeholder="ABCDE1234F" maxLength={10}
                className="h-11 bg-black/40 border-white/15 font-mono uppercase"
              />
            </Field>
          </div>
          <AffiliationsPicker
            section="production_house"
            value={sectionAffiliations}
            onChange={setSectionAffiliations}
            testidPrefix="onboard-aff-production-house"
          />
          <NavRow onNext={next} disabledNext={!legal || sectionAffiliations.length === 0} />
        </Card>
      )}

      {step === 1 && (
        <Card title="Slate Specialty" testid="step-slate">
          <div className="flex items-start gap-3 mb-2 p-3 rounded-lg bg-red-500/8 border border-red-500/30">
            <Film size={16} className="text-red-300 shrink-0 mt-0.5" />
            <div className="text-xs text-white/75 leading-relaxed">
              Tick the project formats your Production House originates.
              This drives which project templates get suggested on
              &apos;/projects/init&apos; and how you show up in producer search.
              Pick at least one.
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="ph-slate-grid">
            {projectTypes.map((t) => {
              const on = slate.has(t.key);
              return (
                <button key={t.key} onClick={() => toggleSlate(t.key)}
                  className={`p-4 rounded-xl text-left border transition-colors ${
                    on
                      ? "bg-[#D9383A]/12 border-[#D9383A]/55"
                      : "bg-black/30 border-white/10 hover:bg-white/[0.05]"
                  }`}
                  data-testid={`ph-slate-${t.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-white">{t.label}</div>
                    {on && <Check size={16} className="text-[#D9383A]" />}
                  </div>
                </button>
              );
            })}
          </div>
          <NavRow onBack={back} onNext={next} disabledNext={slate.size === 0} />
        </Card>
      )}

      {step === 2 && (
        <Card title="Verification Documents" testid="step-docs">
          <p className="text-sm text-white/65 leading-relaxed">
            Production Houses are gated by KYC so no fake studios can verify credentials. In v1
            we accept mocked uploads — drop your <strong>Company Incorporation Certificate</strong>,
            <strong> GST Registration</strong>, and <strong>PAN</strong>. Real upload + manual
            review goes live in v2.
          </p>

          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            {["Incorporation Certificate", "GST Registration", "PAN Card"].map((d) => (
              <div key={d} className="cs-card rounded-xl p-5 text-center" data-testid={`doc-${d.replace(/\s+/g, "-").toLowerCase()}`}>
                <Upload size={20} className="mx-auto text-white/40" />
                <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-white/55">{d}</div>
                <div className="mt-3 text-[10px] text-white/40">
                  PDF/JPG · 5 MB max
                </div>
                <div className="mt-3 inline-block text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">
                  Mocked upload
                </div>
              </div>
            ))}
          </div>

          <label className="mt-6 flex items-start gap-2.5 text-sm text-white/75 cursor-pointer">
            <input
              type="checkbox" checked={docsAck} onChange={(e) => setDocsAck(e.target.checked)}
              data-testid="docs-ack" className="mt-0.5"
            />
            <span>
              I confirm the organization is a real, registered entity. I understand v1 auto-approves
              for demo purposes — real verification will be required before going to production.
            </span>
          </label>

          <NavRow onBack={back} onNext={next} disabledNext={!docsAck} />
        </Card>
      )}

      {step === 3 && (
        <Card title="Owner Account" testid="step-owner">
          <p className="text-sm text-white/65 leading-relaxed">
            This account is the Owner. You&apos;ll be able to invite Producers, ADs,
            Line Producers, Accountants, and Department Heads after sign-up.
          </p>
          <Field label="Full Name *" tid="ph-owner-name">
            <Input data-testid="ph-owner-name-input"
              value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
              className="h-11 bg-black/40 border-white/15" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email *" tid="ph-owner-email">
              <Input data-testid="ph-owner-email-input" type="email"
                value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                className="h-11 bg-black/40 border-white/15" />
            </Field>
            <Field label="Phone (optional)" tid="ph-owner-phone">
              <Input data-testid="ph-owner-phone-input"
                value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="+91 …"
                className="h-11 bg-black/40 border-white/15" />
            </Field>
          </div>
          <Field label="Password * (min 6 characters)" tid="ph-owner-pwd">
            <Input data-testid="ph-owner-pwd-input" type="password"
              value={ownerPwd} onChange={(e) => setOwnerPwd(e.target.value)}
              minLength={6}
              className="h-11 bg-black/40 border-white/15" />
          </Field>

          <NavRow
            onBack={back}
            submitLabel="Create Organization"
            onSubmit={submit}
            submitting={submitting}
            disabledNext={!ownerName || !ownerEmail || ownerPwd.length < 6}
          />
        </Card>
      )}

      {step === 4 && (
        <Card title="" testid="step-approved">
          <div className="text-center py-6" data-testid="onboard-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck size={26} className="text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl uppercase tracking-tight mt-5">
              You&apos;re Verified
            </h2>
            <p className="mt-3 text-sm text-white/70 max-w-md mx-auto">
              Your Production House is live. Spin up your first project, invite your team,
              and start adding crew by phone or association ID.
            </p>
            <Button
              onClick={() => nav("/dashboard")}
              className="mt-7 btn-themed h-11 px-7 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
              data-testid="onboard-go-dashboard"
            >
              Open Dashboard <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="mt-8 mb-8 flex items-center gap-2.5 flex-wrap" data-testid="onboard-stepper">
      {STEPS.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={label} className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
              done ? "bg-emerald-500/25 text-emerald-300"
              : active ? "bg-[var(--association-theme)] text-black"
              : "bg-white/10 text-white/55"
            }`}>
              {done ? <Check size={12} /> : i + 1}
            </div>
            <div className={`text-[10px] tracking-[0.22em] uppercase ${active ? "text-white" : "text-white/45"}`}>
              {label}
            </div>
            {i < STEPS.length - 1 && (
              <div className="w-6 h-px bg-white/15" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="cs-card rounded-2xl p-6 space-y-5" data-testid={testid}>
      {title && (
        <div className="text-[10px] tracking-[0.25em] uppercase text-white/55">{title}</div>
      )}
      {children}
    </div>
  );
}

function Field({ label, children, tid }) {
  return (
    <div data-testid={tid}>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function NavRow({ onBack, onNext, onSubmit, submitting, submitLabel, disabledNext }) {
  return (
    <div className="flex items-center justify-between pt-3">
      {onBack ? (
        <Button onClick={onBack} variant="ghost"
          className="h-10 px-4 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white"
          data-testid="onboard-back">
          <ArrowLeft size={13} className="mr-1.5" /> Back
        </Button>
      ) : <span />}

      {onSubmit ? (
        <Button onClick={onSubmit} disabled={disabledNext || submitting}
          className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
          data-testid="onboard-submit">
          {submitting && <Loader2 size={14} className="mr-2 animate-spin" />}
          {submitLabel || "Submit"} <ArrowRight size={14} className="ml-2" />
        </Button>
      ) : (
        <Button onClick={onNext} disabled={disabledNext}
          className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
          data-testid="onboard-next">
          Continue <ArrowRight size={14} className="ml-2" />
        </Button>
      )}
    </div>
  );
}
